import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as productApi from '../services/product.service';
import type { CreateProductInput, EditProductInput, ProductListQuery } from '../types';

export const productKeys = {
  all: ['admin', 'products'] as const,
  list: (q: ProductListQuery) => [...productKeys.all, 'list', q] as const,
  detail: (id: string) => [...productKeys.all, 'detail', id] as const,
};

export const useProductsList = (q: ProductListQuery) =>
  useQuery({ queryKey: productKeys.list(q), queryFn: () => productApi.listProducts(q) });

export const useProductDetail = (id: string | undefined) =>
  useQuery({
    queryKey: productKeys.detail(id ?? ''),
    queryFn: () => productApi.getProduct(id as string),
    enabled: !!id,
  });

export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProductInput) => productApi.createProduct(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: productKeys.all }),
  });
}

export function useEditProduct(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EditProductInput) => productApi.editProduct(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: productKeys.all });
      qc.invalidateQueries({ queryKey: productKeys.detail(id) });
    },
  });
}

/** Distinct mutation from edit — reprice creates a new Collection version (backend §8). */
export function useRepriceProduct(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (amountInPaise: number) => productApi.repriceProduct(id, amountInPaise),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: productKeys.all });
      qc.invalidateQueries({ queryKey: productKeys.detail(id) });
    },
  });
}

export function useActivateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => productApi.activateProduct(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: productKeys.all }),
  });
}

export function useDeactivateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => productApi.deactivateProduct(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: productKeys.all }),
  });
}
